import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { ExclamationCircleIcon } from "@/components/icons/exclamation-circle";
import { ToastContent } from "@/components/toasts/toast-content-component";
import useFetch from "@/hooks/use-fetch";
import endpoints from "@/utils/endpoints";
import { isError } from "@/utils/is-error";
import logger from "@/utils/logger";

type TImageUploadContext = {
  isUploading: boolean;
  uploadImage: (
    string: string,
    file: File
  ) => Promise<{
    uploadResponse?: Response;
    imageUrl?: string;
    imageUsageId?: number;
  }>;
};

type ImageUploadProviderProps = {
  children: React.ReactNode;
};

interface ImageAPIResponse {
  params: Record<string, string>;
  endpoint_url: string;
  image_usage: {
    id: number;
  };
  image: {
    key: string;
    src: string;
  };
}

export const ImageUploadProviderContext = React.createContext<TImageUploadContext>(
  undefined
);

const get = endpoints();

export default function ImageUploadProvider(props: ImageUploadProviderProps) {
  const [isUploading, setIsUploading] = React.useState(false);
  const { authenticatedFetch } = useFetch();
  const { t } = useTranslation();

  const uploadImage = React.useCallback(
    async (slug: string, file: File) => {
      const filename = encodeURIComponent(file.name);
      try {
        setIsUploading(true);
        const response = await authenticatedFetch<{
          params: Record<string, string>;
          endpoint_url: string;
          image_url: string;
        }>(get("PRESIGN_IMAGE_UPLOAD_API")(slug, filename), {
          method: "GET",
        });

        if (isError(response)) {
          toast.error(response.description);
          setIsUploading(false);
          return {};
        }

        const { params, endpoint_url, image_url } = response;

        // Construct Form Data payload with presigned params and file
        const formData = new FormData();
        Object.keys(params).map((k) => {
          formData.append(k, params[k]);
        });
        formData.append("file", file);

        // POST to upload to S3
        const uploadResponse = await fetch(endpoint_url, {
          body: formData,
          method: "POST",
        });
        return {
          uploadResponse,
          imageUrl: image_url,
        };
      } catch (error) {
        logger.error(error, {
          details: "Image upload component",
        });
        toast.error(
          <ToastContent
            icon={ExclamationCircleIcon}
            text={t("preference-panel.image-upload.failed-image-upload")}
          />
        );
        return {};
      } finally {
        setIsUploading(false);
      }
    },
    [authenticatedFetch, setIsUploading, t]
  );

  const contextValue = React.useMemo(() => {
    return {
      isUploading,
      uploadImage,
    };
  }, [isUploading, uploadImage]);

  return (
    <ImageUploadProviderContext.Provider value={contextValue}>
      {props.children}
    </ImageUploadProviderContext.Provider>
  );
}
